import type {
	FlexibleExpectedReturnsVolatilityAssetClassAssetClassEnum,
	InvestmentActions,
	InvestmentNote,
	MarketScenario,
	MarketViewAssetClassIdentifier,
} from "@eu.mdotm.api/sphere";
import type { MinimumCompareProps } from "$root/functional-areas/compare-portfolio/MinimumCompareProps";
import MonitorWithHourglass from "$root/assets/images/glyphs[preload]/MonitorWithHourglass.svg";
import { IconWallBase } from "$root/components/IconWall";
import { applyDeltaToSelectedMarketView } from "$root/components/Portfolio/MarketView/utilsV2";
import ReactQueryWrapper, { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { spawnYesNoDialog } from "$root/components/spawnable/yes-no-dialog";
import { formatDate } from "$root/localization/formatters";
import { useLocaleFormatters } from "$root/localization/hooks";
import { customObjectKeysFn, customObjectValuesFn, stableColorGenerator } from "$root/utils";
import { noRefetchDefaults } from "$root/third-party-integrations/react-query/hooks";
import {
	type ExposureCategoryState,
	ExposureCategorySelector,
	ExposureChart,
	WithExposureCategories,
	aggregateExposureData,
} from "$root/widgets-architecture/widgets/ExposureEvolve";
import type { InvestmentExposureResponseExposureTypeEnum } from "$root/widgets-architecture/widgets/ExposureEvolve/InvestmentExposureResponseExposureTypeEnum";
import {
	aggregateHistory,
	spawnPortfolioHistoryDialog,
	typeToStringMap,
} from "$root/widgets-architecture/widgets/PortfolioHistoryBlock";
import {
	type DefaultCollapsibleHeaderProps,
	type TextProps,
	ActionText,
	CircularProgressBar,
	Collapsible,
	Icon,
	LinkButton,
	ScrollWrapper,
	StackingContext,
	Svg,
	Text,
} from "@mdotm/shadcn/legacy/components";
import {
	type ClassList,
	type NodeOrFn,
	generateUniqueDOMId,
	renderNodeOrFn,
	toClassListRecord,
	toClassName,
	useEventListener,
} from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach } from "@mdotm/utils/react-extra";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { stableEmptyArray } from "@mdotm/shadcn/legacy/utils";
import { groupBy } from "@mdotm/utils/collections";
import { useQueries } from "@tanstack/react-query";
import { Map, Set } from "immutable";
import { type ReactNode, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { positioningPresets, positioningValueTypeMap } from "../market-view/PositioningBadge";
import { SentimentBadge } from "../market-view/analysis/SentimentBadge";
import type { SentimentType } from "../market-view/analysis/sentiment";
import { marketViewRepository } from "../market-view/repository";
import { portfolioRepository } from "../portfolio/repository";
import { useUserValue } from "../user";
import PortfolioExposureSummary, { type MockMacroComposition } from "./PortfolioExposureSummary";

const positioningSentiment: Record<number, SentimentType> = {
	1: "super-negative",
	2: "negative",
	3: "neutral",
	4: "positive",
	5: "super-positive",
};

/** @internal — exported for tests */
export function aggregateMarketViews(markets: Array<MarketScenario | undefined>) {
	const marketViewsGroupedByMacro = markets.map((m) => {
		if (!m?.marketViewType) {
			return {};
		}

		const { flexibleExpectedReturnsVolatility, positioningIndicators, marketViewType } = m;

		if (marketViewType === "EXPECTED_RETURNS_VOLATILITY") {
			const regimes = {
				lower: m?.regimeUserProbability?.a ?? 0,
				upper: (m?.regimeUserProbability?.a ?? 0) + (m?.regimeUserProbability?.b ?? 0),
			};

			const appliedAssetClasses = applyDeltaToSelectedMarketView(
				{
					a: regimes.lower,
					b: regimes.upper - regimes.lower,
					c: 100 - regimes.upper,
				},
				flexibleExpectedReturnsVolatility?.assetClasses,
			);

			const groups = groupBy(appliedAssetClasses ?? [], (x) => x.assetClass!);
			return { marketViewType, groups };
		}

		const groups = groupBy(positioningIndicators?.positioningIndicators ?? [], (x) => x.assetClass!);
		return { marketViewType, groups };
	});

	const { assetClassesMap } = marketViewsGroupedByMacro.reduce<{
		assetClassesMap: Map<
			FlexibleExpectedReturnsVolatilityAssetClassAssetClassEnum,
			Set<MarketViewAssetClassIdentifier>
		>;
	}>(
		(acc, x) => {
			if (x.marketViewType === "EXPECTED_RETURNS_VOLATILITY") {
				const entries = customObjectValuesFn(x.groups).flat();
				entries.forEach((entry) => {
					if (entry?.assetClass && entry.microAssetClass) {
						const acEntry = acc.assetClassesMap.get(entry.assetClass);
						if (acEntry) {
							acc.assetClassesMap = acc.assetClassesMap.set(entry.assetClass, acEntry.add(entry.microAssetClass));
						}

						if (!acEntry) {
							acc.assetClassesMap = acc.assetClassesMap.set(entry.assetClass, Set([entry.microAssetClass]));
						}
					}
				});
			}

			if (x.marketViewType === "POSITIONING_INDICATORS") {
				const entries = customObjectValuesFn(x.groups).flat();
				entries.forEach((entry) => {
					if (entry?.assetClass && entry.microAssetClass) {
						const acEntry = acc.assetClassesMap.get(entry.assetClass);
						if (acEntry) {
							acc.assetClassesMap = acc.assetClassesMap.set(entry.assetClass, acEntry.add(entry.microAssetClass));
						}

						if (!acEntry) {
							acc.assetClassesMap = acc.assetClassesMap.set(entry.assetClass, Set([entry.microAssetClass]));
						}
					}
				});
			}

			return acc;
		},
		{ assetClassesMap: Map() },
	);

	const macrosAssetClasses = Array.from(assetClassesMap.keys()).map((macro) => macro);

	return { macrosAssetClasses, assetClassesMap, marketViewsGroupedByMacro };
}

export type CompareDataItem = {
	id: string;
	portfolioName: string;
	description?: string;
	composition: Array<MockMacroComposition>;
	numberOfInstrument: number;
	uuid?: string;
	enhanced?: boolean;
	note?: InvestmentNote;
	action?: InvestmentActions;
};

export type CompareOverlayProps = {
	show: boolean;
	onClose(): void;
	onRemove?(compareDataId: string, compareDataIndex: number): void;
	compareData: Array<CompareDataItem>;
};

export function CompareOverlay({ compareData, onRemove, onClose, show }: CompareOverlayProps): React.JSX.Element {
	const [expand, setExpand] = useState(false);
	const { formatNumber } = useLocaleFormatters();
	const { t } = useTranslation();
	const user = useUserValue();

	const investmentsSummaryQuery = useQueries({
		queries: compareData.map((x) => ({
			enabled: x.uuid !== undefined,
			queryKey: [`querySummary(${x.id})`],
			queryFn: () =>
				portfolioRepository.summary.fetchQuery(x.uuid!, { enhanced: x.enhanced ?? false }).then((res) => ({
					...res,
					numberOfInstrument: x.numberOfInstrument,
					id: x.id,
					enhanced: x.enhanced,
				})),
			...noRefetchDefaults,
		})),
	});

	const investmentSummary = investmentsSummaryQuery.map(({ data }) => data);
	const isLoadingSummary = investmentsSummaryQuery.some(({ isLoading }) => isLoading);

	const marketViewsQuery = useQueries({
		queries: investmentSummary.map((item) => ({
			enabled:
				item?.uuid !== undefined &&
				item.marketView?.id !== undefined &&
				item.marketView.marketViewType !== undefined &&
				item.marketView.userCustomScenario !== undefined,
			queryKey: [`queryMarketView(${item?.id})`],
			queryFn: () =>
				marketViewRepository.scenario.fetchQuery({
					scenarioId: item!.marketView!.id!,
					investmentUuid: item!.uuid!,
					custom: item!.marketView!.userCustomScenario!,
					useEnhancement: item?.enhanced,
				}),
			...noRefetchDefaults,
		})),
	});

	const [exposureCategory, setExposureCategory] = useState<ExposureCategoryState>({
		firstLevel: "RISK_MODEL",
		secondLevel: "MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS" satisfies InvestmentExposureResponseExposureTypeEnum,
	});

	const exposureQueries = useQueries({
		queries: investmentSummary.map((item) => ({
			queryKey: [
				"queryExposureLevelEvolve",
				item?.uuid,
				item?.enhanced,
				item?.primaryBenchmarkIdentifier,
				exposureCategory,
				item?.status,
				exposureCategory.secondLevel,
			],
			enabled: item?.uuid !== undefined && item.primaryBenchmarkIdentifier !== undefined,
			keepPreviousData: true,
			queryFn: () =>
				portfolioRepository.exposure.fetchQuery(item!.uuid!, {
					enhanced: item?.enhanced ?? false,
					benchmarkId: item?.primaryBenchmarkIdentifier ?? null,
					secondLevel: exposureCategory.secondLevel,
				}),
		})),
	});

	const marketViews = useMemo(() => marketViewsQuery.map(({ data }) => data), [marketViewsQuery]);
	const aggregatedMarketViews = useMemo(() => aggregateMarketViews(marketViews), [marketViews]);

	const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);

	useEventListener(scrollEl, "scroll", () => {
		if (!scrollEl) {
			return;
		}
		if (scrollEl.scrollLeft > 2) {
			scrollEl.setAttribute("data-show-left-header-shadow", "1");
		} else {
			scrollEl.removeAttribute("data-show-left-header-shadow");
		}
		if (scrollEl.scrollTop > 2) {
			scrollEl.setAttribute("data-show-top-header-shadow", "1");
		} else {
			scrollEl.removeAttribute("data-show-top-header-shadow");
		}
	});

	if (compareData.length < 1 || !show) {
		return <></>;
	}

	return createPortal(
		<StackingContext.Consumer>
			{({ zIndex }) => (
				<div
					style={{
						zIndex,
						borderColor: themeCSSVars.palette_N200,
					}}
					className={toClassName({
						"fixed bottom-0 inset-x-0 border-t transition-[height] flex flex-col shadow-2xl bg-white": true,
						"h-[220px]": !expand,
						"h-[90vh]": expand,
					})}
				>
					<StackingContext.Provider value={{ zIndex: zIndex + 1 }}>
						<div
							className="flex items-center justify-end"
							style={{
								backgroundColor: themeCSSVars.palette_N20,
							}}
						>
							<button
								onClick={() => {
									onClose();
									setExpand(false);
								}}
								type="button"
								className="p-4"
								data-qualifier="CompareOverlay/Close"
							>
								<Icon icon="Close" size={16} color={themeCSSVars.palette_N400} />
							</button>
						</div>
						<ScrollWrapper
							innerRef={setScrollEl}
							direction="horizontal"
							showScrollingArrows
							outerContainerAppearance={{
								className: "min-h-0 h-full",
							}}
							className={{
								"overflow-y-auto min-h-0 group": true,
								"overflow-y-hidden": false,
							}}
							startShadow
							endShadow
						>
							<div
								className="flex flex-row sticky group-data-show-top-header-shadow:shadow-lg z-10 top-0 min-w-[calc(100vw-8px)]"
								style={{
									backgroundColor: themeCSSVars.palette_N20,
								}}
							>
								<div
									className={toClassName({
										"w-[160px] pb-4 flex flex-col px-4 sticky z-10 left-0 group-data-show-left-header-shadow:shadow-lg": true,
										"h-[172px]": expand === false,
										"h-[152px]": expand,
									})}
									style={{
										backgroundColor: themeCSSVars.palette_N20,
									}}
								>
									<Text as="div" type="Title/S" className="whitespace-pre-line">
										Compare{"\n"}portfolios
									</Text>
									{expand ? (
										// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
										<div
											className="flex items-center mt-auto space-x-1 cursor-pointer"
											onClick={() => setExpand(false)}
										>
											<ActionText data-qualifier="CompareOverlay/ViewLess" onClick={() => setExpand(false)}>
												View less
											</ActionText>
											<Icon icon="Down" />
										</div>
									) : isLoadingSummary ? (
										<div className="flex items-center mt-auto space-x-1 pointer-events-none">
											<CircularProgressBar value="indeterminate" className="w-4 h-4" />
										</div>
									) : (
										// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
										<div className="flex items-center mt-auto space-x-1 cursor-pointer" onClick={() => setExpand(true)}>
											<ActionText data-qualifier="CompareOverlay/ViewMore" onClick={() => setExpand(true)}>
												View more
											</ActionText>
											<Icon icon="Up" />
										</div>
									)}
								</div>

								<ForEach collection={compareData}>
									{(item, index) => (
										<div className="w-[312px] border-r border-transparent px-4 mb-4">
											<div
												className={toClassName({
													"shadow-md bg-white rounded-md p-2 flex flex-col": true,
													"rounded-tl rounded-tr border-t-4": true,
													[`border-[color:var(--palette-N200)]`]: !item.enhanced,
													[`border-[color:var(--palette-S200)]`]: item.enhanced,
													"h-full": expand === false,
													"max-h-[115px] h-full": expand,
												})}
											>
												<ReactQueryWrapper {...portfolioRepository.comparisonDetails.getQueryConfig(item.uuid!)}>
													{({ macroAssetClassExposure, macroAssetClassExposureEnhanced }) => (
														<PortfolioExposureSummary
															onRemove={onRemove && (() => onRemove(item.id, index))}
															composition={
																(item.enhanced ? macroAssetClassExposureEnhanced : macroAssetClassExposure) ?? []
															}
															title={item.portfolioName}
															subtitleClassName={item.enhanced ? `!text-[color:var(--palette-S400)]` : undefined}
															note={expand ? undefined : item.note}
															action={expand ? undefined : item.action}
														/>
													)}
												</ReactQueryWrapper>
											</div>
										</div>
									)}
								</ForEach>
							</div>

							<CollapsibleSection
								title="Main info"
								items={investmentSummary}
								leftHeaderContentClassName="flex flex-col"
								leftHeaderContent={() => (
									<div className="pr-2">
										<ForEach collection={["Benchmark", "Universe", "Currency", "N. of instruments", "VaR(95% 3Y)"]}>
											{(item) => (
												<Text
													type="Body/S/Book"
													as="div"
													className={`py-1.5 border-dotted border-b border-[color:var(--palette-N200)] truncate`}
													title={item}
												>
													{item}
												</Text>
											)}
										</ForEach>
									</div>
								)}
								cellContentClassName="flex flex-col"
								cellContent={({ item }) => (
									<ForEach
										collection={[
											item?.primaryBenchmarkName,
											item?.universeName,
											item?.baseCurrency,
											item?.numberOfInstrument,
											`${formatNumber(item?.historicalVar953Y, 0)}%`,
										]}
									>
										{(quality) => (
											<Text
												type="Body/S/Bold"
												as="div"
												className={`py-1.5 border-dotted border-b border-[color:var(--palette-N200)] truncate`}
											>
												{quality ?? "-"}
											</Text>
										)}
									</ForEach>
								)}
							/>

							<CollapsibleSection
								title="Exposure"
								items={investmentSummary}
								extraTitleContent={
									<WithExposureCategories>
										{(exposureOptions) => (
											<>
												<ExposureCategorySelector
													exposureCategory={exposureCategory}
													setExposureCategory={setExposureCategory}
													exposureCategories={exposureOptions}
												/>
												{exposureQueries.some((q) => q.isFetching) && (
													<CircularProgressBar value="indeterminate" outerDiameter={16} />
												)}
											</>
										)}
									</WithExposureCategories>
								}
								cellContentClassName="flex flex-col"
								cellContent={({ item, index }) =>
									exposureQueries[index] && (
										<ReactQueryWrapperBase query={exposureQueries[index]}>
											{(exposureData) =>
												!exposureData ? (
													<div className="w-full grow min-h-0 pt-2">
														<IconWallBase>
															<div className="flex-1 min-h-0 flex flex-col">
																<img alt="illustration" src={MonitorWithHourglass} className="mx-auto my-0" />
																<Text as="div" className="px-1 text-center" type="Body/M/Book">
																	Data not available for the selected comparison, please pick another option.
																</Text>
															</div>
														</IconWallBase>
													</div>
												) : (
													<ExposureChart
														data={Object.values(
															aggregateExposureData(
																(item?.enhanced
																	? exposureData.enhancementComposition
																	: exposureData.investmentComposition) ?? [],
															),
														)}
														hideLabel
													/>
												)
											}
										</ReactQueryWrapperBase>
									)
								}
							/>

							<CollapsibleSection
								title="History"
								items={investmentSummary}
								cellContentClassName="flex flex-col"
								cellContent={({ item }) => (
									<ReactQueryWrapper
										{...portfolioRepository.historyEntries.getQueryConfig(item?.uuid ?? "")}
										enabled={item?.uuid !== undefined}
									>
										{(response, { refetch }) => {
											const events = aggregateHistory(response);
											const latestEvent = events[0];
											return (
												<div className="h-40 flex flex-col py-4">
													<Text type="Body/M/Bold" as="p">
														{typeToStringMap[latestEvent.type].title}{" "}
														<Text type="Body/M/Book" as="span">
															{formatDate(latestEvent.date)}
														</Text>
													</Text>
													{latestEvent.note && (
														<Text type="Body/S/Bold" as="p">
															Note (Created by {latestEvent.note.author?.name ?? ""} on{" "}
															{formatDate(latestEvent.note.date)})
														</Text>
													)}
													<p className="line-clamp-4">{latestEvent.note?.note}</p>
													<LinkButton
														onClick={() =>
															spawnPortfolioHistoryDialog({
																events,
																user,
																portfolio: { ...item },
																onEditNote: async (historyItemId, note) => {
																	await portfolioRepository.editHistoryEntryNote(item!.uuid!, {
																		uuid: historyItemId,
																		note,
																	});
																	const newEvents = await refetch();
																	return aggregateHistory(newEvents.data ?? stableEmptyArray);
																},
																onDeleteNote: async (historyItemId) => {
																	const shouldDelete = await spawnYesNoDialog({
																		header: "Are you sure you want to delete this item?",
																		children: (
																			<p>
																				This will permanently remove the note.{"\n"}Please be aware that this action
																				cannot be undone.
																			</p>
																		),
																	});
																	if (shouldDelete) {
																		await portfolioRepository.deleteHistoryEntryNote(item!.uuid!, {
																			uuid: historyItemId,
																		});
																		const newEvents = await refetch();
																		return aggregateHistory(newEvents.data ?? stableEmptyArray);
																	}
																	return events;
																},
															})
														}
														className="w-fit mt-auto"
														size="x-small"
													>
														View all history
													</LinkButton>
												</div>
											);
										}}
									</ReactQueryWrapper>
								)}
							/>

							{aggregatedMarketViews.macrosAssetClasses.length > 0 && (
								<Collapsible
									expand
									header={(forward) => <CollapsibleSectionHeader title="Market views" {...forward} />}
								>
									<ForEach collection={aggregatedMarketViews.macrosAssetClasses}>
										{(macroAssetClass) => {
											const microCollection =
												aggregatedMarketViews.assetClassesMap.get(macroAssetClass)?.toArray() ?? [];
											return (
												<CollapsibleSection
													titleTextType="Body/XL/Bold"
													title={
														<div className="flex items-center gap-2">
															<Svg
																className="flex min-w-[8px]"
																viewBox={{
																	width: 8,
																	height: 8,
																}}
																fill="none"
															>
																<circle
																	opacity="0.3"
																	r="4"
																	cx="4"
																	cy="4"
																	fill={stableColorGenerator(macroAssetClass)}
																/>
															</Svg>
															<div>{t(`MACRO_ASSET_CLASSES.${macroAssetClass}`)}</div>
														</div>
													}
													items={aggregatedMarketViews.marketViewsGroupedByMacro ?? []}
													leftHeaderContentClassName="flex flex-col overflow-hidden"
													leftHeaderContent={() => (
														<ForEach collection={microCollection}>
															{(groupedMicroAssetClasses) => (
																<div
																	className={`h-[40px] flex items-center overflow-hidden border-[color:var(--palette-N50)] border-b`}
																>
																	<Text
																		type="Body/S/Book"
																		as="p"
																		className="flex-1 line-clamp-2 overflow-hidden"
																		title={t(`MARKET_STUDIO_SETTINGS.${groupedMicroAssetClasses}`)}
																	>
																		{t(`MARKET_STUDIO_SETTINGS.${groupedMicroAssetClasses}`)}
																	</Text>
																</div>
															)}
														</ForEach>
													)}
													cellContentClassName="flex flex-col"
													cellContent={({ item }) =>
														item.marketViewType === "EXPECTED_RETURNS_VOLATILITY" ? (
															<ForEach collection={microCollection}>
																{(micro) => {
																	const entry = item.groups[macroAssetClass]?.find((x) => x.microAssetClass === micro);
																	return (
																		<div
																			className={`h-[40px] flex items-center truncate w-full flex-1 border-[color:var(--palette-N50)] border-b`}
																		>
																			{entry && (
																				<div className="text-right w-full tabular-nums flex space-x-0.5 items-center">
																					{formatNumber(entry.expectedReturnsUserDelta, 2)}
																					&nbsp;
																					<Icon icon="Percentile" color={themeCSSVars.palette_N300} size={16} />
																				</div>
																			)}
																		</div>
																	);
																}}
															</ForEach>
														) : item.marketViewType === "POSITIONING_INDICATORS" ? (
															<ForEach collection={microCollection}>
																{(micro) => {
																	const entry = item.groups[macroAssetClass]?.find((x) => x.microAssetClass === micro);

																	const positioning = customObjectKeysFn(positioningValueTypeMap).find(
																		(index) => entry?.userPositioning && Number(index) === entry.userPositioning,
																	);

																	return (
																		<div
																			className={`"h-[40px] flex items-center truncate w-full flex-1 border-[color:var(--palette-N50)] border-b`}
																		>
																			{positioning ? (
																				<SentimentBadge
																					sentiment={positioningSentiment[positioning]}
																					indicator="positioning"
																				>
																					{positioningPresets[positioningValueTypeMap[positioning]].label}
																				</SentimentBadge>
																			) : (
																				""
																			)}
																		</div>
																	);
																}}
															</ForEach>
														) : (
															<></>
														)
													}
												/>
											);
										}}
									</ForEach>
								</Collapsible>
							)}
						</ScrollWrapper>
					</StackingContext.Provider>
				</div>
			)}
		</StackingContext.Consumer>,
		document.body,
	);
}

type CollapsibleSectionProps<TCollapsible> = {
	title: ReactNode;
	titleTextType?: TextProps["type"];
	extraTitleContent?: ReactNode;
	items: TCollapsible[];
	leftHeaderContent?: NodeOrFn;
	leftHeaderContentClassName?: ClassList;
	cellContent: NodeOrFn<{ item: TCollapsible; index: number }>;
	cellContentClassName?: ClassList;
};

function CollapsibleSection<TCollapsible>({
	title,
	titleTextType,
	extraTitleContent,
	leftHeaderContent,
	leftHeaderContentClassName,
	cellContent,
	cellContentClassName,
	items,
}: CollapsibleSectionProps<TCollapsible>) {
	return (
		<Collapsible
			expand
			header={(forward) => (
				<CollapsibleSectionHeader
					{...forward}
					titleTextType={titleTextType}
					title={title ?? undefined}
					extraTitleContent={extraTitleContent}
				/>
			)}
		>
			<div className="flex flex-row pt-2">
				<div
					className={toClassName({
						"w-[160px] sticky left-0 px-4 bg-white": true,
						"group-data-show-left-header-shadow:shadow-lg": Boolean(leftHeaderContent),
						...toClassListRecord(leftHeaderContentClassName),
					})}
				>
					{renderNodeOrFn(leftHeaderContent)}
				</div>
				<ForEach collection={items}>
					{(item, index) => (
						<div
							className={toClassName({
								"w-[312px] px-4 border-r border-dotted": true,
								[`border-[color:var(--palette-N300)]`]: index < items.length - 1,
								"border-transparent": index === items.length - 1,
								...toClassListRecord(cellContentClassName),
							})}
						>
							{renderNodeOrFn(cellContent, { item, index })}
						</div>
					)}
				</ForEach>
			</div>
		</Collapsible>
	);
}

function CollapsibleSectionHeader({
	titleTextType = "Title/S",
	title,
	extraTitleContent,
	innerRef,
	onClick,
	transitionDuration,
	expand,
}: Omit<DefaultCollapsibleHeaderProps, "children" | "title"> & {
	title: ReactNode;
	titleTextType?: TextProps["type"];
	extraTitleContent?: ReactNode;
}) {
	return (
		<div className="border-b">
			<div className="px-4 py-4 flex items-center gap-4 sticky left-0 w-min">
				<button
					type="button"
					onClick={onClick}
					ref={innerRef as any}
					className="flex items-center gap-4"
					style={{
						borderColor: themeCSSVars.palette_N50,
					}}
				>
					<Text type={titleTextType} color={themeCSSVars.palette_N700} as="div" className="whitespace-nowrap">
						{title}
					</Text>
					<span
						style={{
							transitionDuration: `${transitionDuration}ms`,
						}}
						className="transition-transform aria-expanded:transform-[rotateX(180deg)] flex"
						aria-expanded={expand}
					>
						<Icon icon="Down" color={themeCSSVars.palette_N400} size={24} />
					</span>
				</button>
				{extraTitleContent}
			</div>
		</div>
	);
}

export function generateCompareItem(portfolio: MinimumCompareProps, enhanced: boolean): CompareDataItem {
	return {
		id: generateUniqueDOMId(),
		composition:
			(enhanced ? portfolio?.macroAssetClassExposureEnhanced : portfolio?.macroAssetClassExposure)?.map((x) => ({
				quality: x.firstQualityLevel,
				weight: x.weight,
			})) ?? [],
		numberOfInstrument: portfolio.nofInstruments ?? 0,
		portfolioName: portfolio.name ?? "-",
		uuid: portfolio.uuid,
		enhanced,
		note: portfolio.lastActionNote,
		action: portfolio.action,
	};
}
